import { prepareMediaFile, SelectedMedia } from '@pletly/common';
import { User } from '%context';
import { mapMediaForOptimisticResponse } from '%helpers/optimisticResponse';
import { client } from '../../../api/apollo';
import {
	GetConversationMessagesDocument,
	GetConversationMessagesQuery,
} from '../__generated__/messages.generated';
import {
	SendMessageDocument,
	SendMessageMutation,
	SendMessageMutationVariables,
} from '../__generated__/sendMessage.generated';

type SendMessageData = Pick<SendMessageMutationVariables, 'roomId' | 'text'> & {
	selectedMedia: SelectedMedia[];
	author: User;
};
export const getSendMessageMutation = async ({
	roomId,
	text,
	selectedMedia,
	author,
}: SendMessageData) => {
	const now = Date.now();

	const mediaFiles = selectedMedia
		? await Promise.all(selectedMedia.map(prepareMediaFile))
		: undefined;

	return client.mutate<SendMessageMutation, SendMessageMutationVariables>({
		mutation: SendMessageDocument,
		refetchQueries: [GetConversationMessagesDocument],
		variables: {
			roomId,
			text,
			media: mediaFiles,
		},
		optimisticResponse: {
			sendMessage: {
				id: `temp-id-${now}}`,
				__typename: 'ChatMessage',
				content: text || '',
				media: mapMediaForOptimisticResponse(selectedMedia, now),
				from: {
					id: author.id,
					__typename: 'User',
				},
				createdAt: now,
			},
		},
		update: (cacheProxy, { data: mutationData }) => {
			if (!mutationData) {
				return;
			}

			const conversationInCache: GetConversationMessagesQuery | null = cacheProxy.readQuery({
				query: GetConversationMessagesDocument,
				variables: { conversationId: roomId },
			});
			if (!conversationInCache) {
				return;
			}

			const { sendMessage: sentMessage } = mutationData;
			if (!conversationInCache.conversation) {
				return;
			}

			const sentMessageWithAuthor = {
				...sentMessage,
				from: {
					...author,
					...sentMessage.from,
				},
			};

			cacheProxy.writeQuery<GetConversationMessagesQuery>({
				query: GetConversationMessagesDocument,
				data: {
					...conversationInCache,
					conversation: {
						...conversationInCache?.conversation,
						messages: {
							...conversationInCache?.conversation.messages,
							edges: [
								...(conversationInCache?.conversation?.messages?.edges || []),
								{
									cursor: 'temp-cursor',
									node: sentMessageWithAuthor,
									__typename: 'ChatMessageEdge',
								},
							],
						},
					},
				},
			});
		},
	});
};
